import { MONK_SWATCHES } from '@omnialera/ori-signal';
import { useI18n } from '../lib/i18n.jsx';

/**
 * The language picker.
 *
 * Placed in the sidebar rather than buried in a settings page, because the
 * person who most needs it is the one who cannot read the settings page. Each
 * option is written in its own language — a picker that says "Yoruba" in
 * English is asking you to read English to escape English.
 */
export function LanguagePicker() {
  const { code, setCode, locales, t } = useI18n();
  return (
    <div className="langpick">
      <label htmlFor="ori-lang">{t('app.language')}</label>
      <select id="ori-lang" value={code} onChange={(e) => setCode(e.target.value)}>
        {locales.map((l) => (
          <option key={l.code} value={l.code}>
            {l.endonym}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Stat({ label, value, unit, tone }) {
  return (
    <div className="stat">
      <span className="k">{label}</span>
      <span className="v" style={tone ? { color: tone } : undefined}>
        {value}
        {unit ? <span className="u">{unit}</span> : null}
      </span>
    </div>
  );
}

export function Pill({ kind = 'off', children }) {
  return <span className={`pill ${kind}`}>{children}</span>;
}

export function Toggle({ id, checked, disabled, onChange, title, description, earn }) {
  return (
    <div className="tog">
      <button
        type="button"
        id={id}
        className="sw"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
      <div className="txt">
        <b>{title}</b>
        <span>{description}</span>
        {earn ? <span className="earn">{earn}</span> : null}
      </div>
    </div>
  );
}

export function TonePicker({ value, onChange }) {
  const { t } = useI18n();
  return (
    <>
      {/* A radiogroup, not a row of buttons: arrow keys move between swatches
          and a screen reader announces "3 of 10" rather than "button". */}
      <div className="tones" role="radiogroup" aria-label={t('tone.group')}>
        {MONK_SWATCHES.map((hex, i) => (
          <button
            type="button"
            key={hex}
            role="radio"
            style={{ background: hex }}
            aria-checked={value === i + 1}
            tabIndex={value === i + 1 || (!value && i === 0) ? 0 : -1}
            aria-label={t('tone.label', { n: i + 1 })}
            onKeyDown={(e) => {
              const delta = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
                : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1
                : 0;
              if (!delta) return;
              e.preventDefault();
              const next = Math.min(10, Math.max(1, (value ?? 1) + delta));
              onChange(next);
              e.currentTarget.parentElement?.children[next - 1]?.focus();
            }}
            onClick={() => onChange(i + 1)}
          />
        ))}
      </div>
      <div className="tonelab">
        <span>{t('tone.lightest')}</span>
        <span>{value ? t('tone.chosen', { n: value }) : t('tone.none')}</span>
        <span>{t('tone.darkest')}</span>
      </div>
    </>
  );
}

export function Why({ children }) {
  return (
    <div className="why">
      <p className="eyebrow">Why this exists</p>
      <p>{children}</p>
    </div>
  );
}

export function Notice({ kind = '', title, children }) {
  return (
    <div className={`notice ${kind}`}>
      {title ? <b>{title}</b> : null}
      <p>{children}</p>
    </div>
  );
}

export function ago(timestamp) {
  const seconds = (Date.now() - new Date(timestamp).getTime()) / 1000;
  if (!Number.isFinite(seconds)) return '';
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

export function percent(value, digits = 0) {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}
